import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faVideo,
  faImage,
  faFileAlt,
  faCheckCircle,
  faLightbulb,
  faArrowRight,
  faPlay,
  faUsers,
  faStar,
  faClock,
  faBook,
  faChartLine,
  faHandshake,
  faGraduationCap,
  faPenToSquare,
  faBookOpen,
  faCircleInfo,
  faTag,
  faLock,
  faRotate,
  faBell,
  faMoneyBillWave,
  faShieldHalved,
  faTriangleExclamation,
  faListCheck,
  faUpload,
  faEye,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';
import './InstructorGuide.css';
import InstaTopbar from '../InstaTopbar/InstaTopbar';

const InstructorGuide = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const navigate = useNavigate();

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: faGraduationCap },
    { id: 'course-creation', title: 'Create Your Course', icon: faPlus },
    { id: 'content-creation', title: 'Build Your Content', icon: faVideo },
    { id: 'pricing-publishing', title: 'Pricing & Publishing', icon: faRocket },
    { id: 'manage-grow', title: 'Manage & Grow', icon: faChartLine },
    { id: 'best-practices', title: 'Best Practices', icon: faStar },
    { id: 'marketing', title: 'Marketing Tips', icon: faBook },
    { id: 'engagement', title: 'Student Engagement', icon: faUsers },
  ];

  const renderGettingStarted = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faGraduationCap} /> Getting Started as an Instructor</h2>

      <div className="guide-card">
        <h3>Welcome to Your Teaching Journey!</h3>
        <p>
          Becoming an instructor is simple. You don’t have to do everything at once — follow the flow below.
          The short version: <strong>become an instructor → plan → create a draft → add lessons → publish → grow</strong>.
        </p>
      </div>

      <div className="steps-container">
        <div className="step">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Become an Instructor</h4>
            <p>
              From the Home page, tap <strong>Instructor</strong> and complete the short instructor
              application (a few questions plus a short bio). Once submitted, the instructor role is
              added to your account and you land on your <strong>Dashboard</strong>.
            </p>
          </div>
        </div>

        <div className="step">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Complete Your Profile</h4>
            <p>Add a clear photo, a short bio, and what you teach. Students trust instructors who look real and professional.</p>
            <Link to="/instructor-about" className="guide-link">
              <FontAwesomeIcon icon={faArrowRight} /> Edit Your Bio
            </Link>
          </div>
        </div>

        <div className="step">
          <div className="step-number">3</div>
          <div className="step-content">
            <h4>Know Your Dashboard</h4>
            <p>
              Your Dashboard is home base. From the sidebar and top menu you can reach
              <strong> Courses</strong>, <strong>Performance</strong>, <strong>Payment</strong>,
              <strong> Notifications</strong>, and this Guide any time.
            </p>
            <Link to="/dashboard" className="guide-link">
              <FontAwesomeIcon icon={faArrowRight} /> Go to Dashboard
            </Link>
          </div>
        </div>

        <div className="step">
          <div className="step-number">4</div>
          <div className="step-content">
            <h4>Plan Your Course</h4>
            <p>
              Decide who this course is for (beginner / intermediate / advanced) and write a simple outline:
              <strong> Sections → Lessons</strong>. Start from the basics and move step-by-step to advanced topics.
            </p>
          </div>
        </div>

        <div className="step">
          <div className="step-number">5</div>
          <div className="step-content">
            <h4>Create, Publish & Track</h4>
            <p>
              Create your course, publish it when it meets the requirements, then use
              <strong> Performance Overview</strong> to watch enrollments and see what students like.
            </p>
            <Link to="/performance" className="guide-link">
              <FontAwesomeIcon icon={faArrowRight} /> Performance Overview
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCourseCreation = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faPlus} /> Create Your Course</h2>

      <div className="guide-card highlight">
        <h3>Ready to Create Your Course?</h3>
        <p>Click below to start a course draft. Nothing goes live yet — you can keep editing until you publish.</p>
        <Link to="/add-new-course" className="cta-button">
          <FontAwesomeIcon icon={faPlus} /> Create New Course
        </Link>
      </div>

      <div className="guide-card">
        <h3>Before you start (quick checklist)</h3>
        <ul>
          <li><strong>Course title:</strong> clear and simple, up to 150 characters</li>
          <li><strong>Short description:</strong> maximum 20 words</li>
          <li><strong>Detailed description:</strong> explain what the course covers</li>
          <li>
            <strong>Introduction video:</strong> MP4, WebM or AVI — <strong>maximum 5 minutes</strong>, 720p or higher recommended.
            <br />
            <small>
              <strong>Recommended format:</strong> <strong>MP4</strong> works on all devices, browsers and players, has a small file size and loads fast.
            </small>
            <br />
            <small>
              <strong>Size:</strong> up to 500MB is allowed, but for a 5‑minute intro we recommend keeping it around 50–200MB for a faster upload.
            </small>
          </li>
          <li><strong>Thumbnail image:</strong> JPEG, PNG, GIF or WebP</li>
          <li><strong>Learner details:</strong> requirements, what students will learn, and target audience</li>
          <li><strong>Category / Subcategory + Language:</strong> pick the right ones so students can find your course</li>
        </ul>
      </div>

      <div className="creation-steps">
        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faCircleInfo} />
          </div>
          <div className="step-details">
            <h4>Step 1: Fill in the course basics</h4>
            <ul>
              <li><strong>Title</strong> (clear + simple, max 150 characters)</li>
              <li><strong>Short description</strong> (max 20 words)</li>
              <li><strong>Detailed description</strong> (what this course covers)</li>
              <li><strong>Category</strong> and <strong>subcategory</strong></li>
              <li><strong>Language</strong> (or choose “Other” and type your language)</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faVideo} />
          </div>
          <div className="step-details">
            <h4>Step 2: Add the intro video + thumbnail</h4>
            <ul>
              <li><strong>Video formats:</strong> MP4, WebM, AVI</li>
              <li><strong>Recommended:</strong> MP4 — best support and fastest loading</li>
              <li><strong>Max duration:</strong> 5 minutes</li>
              <li><strong>Max size:</strong> 500MB (recommended ~50–200MB)</li>
              <li><strong>Recommended quality:</strong> 720p or higher</li>
              <li><strong>Thumbnail formats:</strong> JPEG, PNG, GIF, WebP</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div className="step-details">
            <h4>Step 3: Add learner-focused details</h4>
            <ul>
              <li><strong>Requirements:</strong> what students should know before starting</li>
              <li><strong>What students will learn:</strong> key outcomes (keep each point short)</li>
              <li><strong>Target audience:</strong> who this course is for</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="step-details">
            <h4>Step 4: Submit to create a draft</h4>
            <ul>
              <li>Review your details and check the intro video plays correctly</li>
              <li>After you submit, your course is saved as a <strong>Draft</strong></li>
              <li>You’re taken straight to <strong>Course Details</strong> to keep building</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContentCreation = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faVideo} /> Build Your Content</h2>

      <div className="guide-card">
        <h3>Where you build: Course Details</h3>
        <p>
          After creating the draft you land on the <strong>Course Details</strong> page. From the top toolbar you can edit the
          <strong> Intro</strong>, <strong>Overview</strong> and <strong>Price</strong>, and add
          <strong> Sections</strong>, <strong>Lessons</strong> and <strong>Attachments</strong>. Every change stays in your
          draft until you publish, so experiment freely.
        </p>
      </div>

      <div className="tips-grid">
        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faVideo} />
          </div>
          <h4>Lesson Video Rules (important)</h4>
          <ul>
            <li><strong>Video formats:</strong> MP4, WebM, AVI, MOV</li>
            <li><strong>Recommended format:</strong> <strong>MP4</strong> — supports all devices, browsers and players, small file size and fast loading.</li>
            <li><strong>Minimum quality:</strong> 720p (required — lower-quality videos are blocked)</li>
            <li><strong>Course size options:</strong>
              <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                <li><strong>Single-video course:</strong> maximum 1.5GB for that lesson</li>
                <li><strong>Multi-lesson course:</strong> maximum 2GB total for the whole course</li>
              </ul>
            </li>
            <li>If a video is bigger than 1.5GB, <strong>split it into multiple lessons</strong> (total must stay under 2GB)</li>
            <li>Use clear audio and good lighting</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faBook} />
          </div>
          <h4>Sections & Lessons (how to organize)</h4>
          <ul>
            <li><strong>Flexible:</strong> one section with one lesson, or many sections with many lessons — your choice.</li>
            <li>Create <strong>Sections</strong> for big chapters (e.g. Basics, Advanced, Projects)</li>
            <li>Add <strong>Lessons</strong> inside each section</li>
            <li>Use lesson names that explain the topic (not “Video 1”)</li>
            <li><strong>Recommended:</strong> split long content into shorter lessons — easier to watch and upload</li>
            <li>End with a summary lesson or “next steps”</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faEye} />
          </div>
          <h4>Preview Lessons</h4>
          <ul>
            <li>Mark a lesson as a <strong>Preview</strong> to let students watch it before enrolling</li>
            <li>Choose your strongest, most useful lesson as the preview</li>
            <li>A good preview builds trust and increases enrollments</li>
            <li>Toggle preview on/off any time from the lesson</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faImage} />
          </div>
          <h4>Attachments (notes, slides, resources)</h4>
          <ul>
            <li>Add supporting files to a lesson (notes, slides, resources)</li>
            <li><strong>Supported formats:</strong> PDF, DOC/DOCX, PPT/PPTX, ZIP</li>
            <li>Name attachments clearly (e.g. “Lesson 3 Notes”)</li>
            <li>Only share files you have the rights to distribute</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <h4>Video Length (recommended)</h4>
          <ul>
            <li>Keep one lesson focused on one topic</li>
            <li>Break big topics into smaller lessons</li>
            <li>Add examples and practice steps</li>
            <li>Start each lesson with “what you’ll learn”</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faPenToSquare} />
          </div>
          <h4>Editing Later</h4>
          <ul>
            <li>Use the <strong>Intro</strong> and <strong>Overview</strong> buttons to update text, video and thumbnail</li>
            <li>Areas you’ve changed show a <strong>“Draft”</strong> label</li>
            <li>Delete draft sections/lessons/attachments with their delete buttons</li>
            <li>Changes are saved to your draft until you publish or update</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderPricingPublishing = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faRocket} /> Pricing & Publishing</h2>

      <div className="guide-card" style={{ borderLeftColor: '#d32f2f' }}>
        <h3 style={{ color: '#d32f2f' }}>
          <FontAwesomeIcon icon={faLock} /> Launch Offer: All Courses Are Free
        </h3>
        <p style={{ color: '#d32f2f', fontWeight: 600 }}>
          During our launch offer, every course is published <strong>FREE</strong> and pricing is locked by the platform.
          The price fields are hidden for now — you don’t need to set anything. We’ll let you know when paid pricing opens up.
        </p>
      </div>

      <div className="creation-steps">
        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faTag} />
          </div>
          <div className="step-details">
            <h4>Pricing (how it will work)</h4>
            <ul>
              <li>Right now: all courses are <strong>free</strong> — nothing to configure</li>
              <li>Later, you’ll be able to set an <strong>actual price</strong>, an <strong>offer price</strong> and a <strong>currency</strong></li>
              <li>You’ll also be able to mark a course as <strong>free</strong> whenever you want</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faListCheck} />
          </div>
          <div className="step-details">
            <h4>Publish Requirements</h4>
            <ul>
              <li><strong>At least 1 lesson</strong></li>
              <li><strong>At least 3 minutes</strong> of total video content</li>
              <li><strong>Maximum 2GB</strong> total course size</li>
              <li>If requirements aren’t met, the course stays a <strong>Draft</strong> and the Publish button is disabled</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faBookOpen} />
          </div>
          <div className="step-details">
            <h4>Publishing your course</h4>
            <ul>
              <li>Click <strong>Publish</strong> once requirements are met</li>
              <li>Confirm — <strong>a published course cannot be deleted by you</strong></li>
              <li>Your course then becomes <strong>visible to students</strong></li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faRotate} />
          </div>
          <div className="step-details">
            <h4>Updating a published course</h4>
            <ul>
              <li>Editing a live course saves your edits as <strong>Draft changes</strong></li>
              <li>Students keep seeing the current version until you push updates</li>
              <li>Click <strong>Update Course</strong> to make your latest edits live</li>
              <li>Enrolled students are notified when content changes</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="guide-card">
        <h3>Draft vs Published — at a glance</h3>
        <ul>
          <li><strong>Draft:</strong> only you can see it. You can edit everything and <strong>delete</strong> it.</li>
          <li><strong>Published:</strong> students can see and enroll. You can still edit, but changes go live only after <strong>Update Course</strong>.</li>
          <li><strong>Published courses can’t be self-deleted</strong> — removal happens through an admin request you respond to (see “Manage &amp; Grow”).</li>
        </ul>
      </div>
    </div>
  );

  const renderManageGrow = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faChartLine} /> Manage & Grow</h2>

      <div className="tips-grid">
        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faBook} />
          </div>
          <h4>My Courses</h4>
          <ul>
            <li>See all your courses split into <strong>Published</strong> and <strong>Draft</strong> tabs</li>
            <li><strong>Draft:</strong> Publish, edit details, or Delete</li>
            <li><strong>Published:</strong> open to edit, view student count, and update</li>
            <li>Start a new course any time with <strong>Create New Course</strong></li>
          </ul>
          <Link to="/my-courses" className="guide-link">
            <FontAwesomeIcon icon={faArrowRight} /> Open My Courses
          </Link>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <h4>Performance & Students</h4>
          <ul>
            <li>Track enrollments, ratings, reviews and watch time</li>
            <li>See your top courses and completion rates</li>
            <li>View every enrolled student with search and filters</li>
          </ul>
          <Link to="/performance" className="guide-link">
            <FontAwesomeIcon icon={faArrowRight} /> Performance Overview
          </Link>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <h4>Payments & Earnings</h4>
          <ul>
            <li>See your earnings for this month and overall</li>
            <li>Review your transactions with month/year filters</li>
            <li>Add your bank details so you’re ready for payouts</li>
            <li><small>(Earnings are ₹0 during the free launch offer.)</small></li>
          </ul>
          <Link to="/instructor-payout" className="guide-link">
            <FontAwesomeIcon icon={faArrowRight} /> Payment & Payout
          </Link>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <h4>Notifications</h4>
          <ul>
            <li>Get notified when a student purchases/enrolls in your course</li>
            <li>See test-video review results (approved / rejected) with admin comments</li>
            <li>The bell shows a badge when you have unread items</li>
          </ul>
          <Link to="/Instructor-notifications" className="guide-link">
            <FontAwesomeIcon icon={faArrowRight} /> View Notifications
          </Link>
        </div>

        <div className="tip-card" style={{ borderTopColor: '#d32f2f' }}>
          <div className="tip-icon" style={{ background: 'linear-gradient(135deg, #d32f2f, #ef6c6c)' }}>
            <FontAwesomeIcon icon={faShieldHalved} />
          </div>
          <h4>Course Deletion Requests</h4>
          <ul>
            <li>An admin may request removal of a published course, with a reason</li>
            <li>You’ll get a <strong>notification and an email</strong> with a secure link</li>
            <li>You can <strong>Confirm deletion</strong> or <strong>Request time to update</strong> (about 7 days)</li>
            <li>If you don’t respond in time, the admin may proceed</li>
            <li>Respond promptly to keep control of your course</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h4>Share Feedback</h4>
          <ul>
            <li>Send suggestions, compliments or issues to the team</li>
            <li>Pick a category and describe what’s on your mind</li>
            <li>Helps us improve the instructor experience</li>
          </ul>
          <Link to="/instructor-feedback" className="guide-link">
            <FontAwesomeIcon icon={faArrowRight} /> Give Feedback
          </Link>
        </div>
      </div>
    </div>
  );

  const renderBestPractices = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faStar} /> Best Practices</h2>

      <div className="practices-container">
        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </div>
          <div className="practice-content">
            <h4>Follow the platform rules (saves time)</h4>
            <p>
              Keep the intro video within <strong>5 minutes</strong> and use supported formats (MP4/WebM/AVI).
              For lessons, use at least <strong>720p</strong> quality, keep each lesson focused on one topic,
              and make sure your course has at least <strong>1 lesson</strong> and <strong>3 minutes</strong> of video before publishing.
            </p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faLightbulb} />
          </div>
          <div className="practice-content">
            <h4>Clear Learning Objectives</h4>
            <p>Define what students will learn by the end of your course. Make objectives specific, measurable, and achievable.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="practice-content">
            <h4>Know Your Audience</h4>
            <p>Tailor your content to your target audience's skill level, background, and learning goals.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faUpload} />
          </div>
          <div className="practice-content">
            <h4>Upload Smart</h4>
            <p>
              Export videos as MP4 to keep files small and uploads fast. If a lesson is over 1.5GB, split it into
              shorter lessons so the whole course stays under the 2GB limit.
            </p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faPlay} />
          </div>
          <div className="practice-content">
            <h4>Engaging Delivery</h4>
            <p>Use a conversational tone, include real-world examples, and maintain student engagement throughout.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="practice-content">
            <h4>Quality Assurance</h4>
            <p>Review your content multiple times, test your videos, and ensure all information is accurate and up-to-date before publishing.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketing = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faBook} /> Marketing Your Course</h2>

      <div className="marketing-tips">
        <div className="marketing-card">
          <h4>Compelling Course Title</h4>
          <p>Create a title that clearly communicates the value and outcome students will get from your course.</p>
        </div>

        <div className="marketing-card">
          <h4>Detailed Description</h4>
          <p>Write a comprehensive description that highlights the benefits, includes testimonials if available, and addresses common pain points.</p>
        </div>

        <div className="marketing-card">
          <h4>Professional Thumbnail</h4>
          <p>Design an eye-catching thumbnail that represents your course content and appeals to your target audience.</p>
        </div>

        <div className="marketing-card">
          <h4>Strong Preview Lesson</h4>
          <p>Set your best lesson as a free preview. A great preview lets students sample your teaching and boosts enrollments.</p>
        </div>

        <div className="marketing-card">
          <h4>Social Media Promotion</h4>
          <p>Share your course on social media platforms, educational forums, and relevant online communities.</p>
        </div>

        <div className="marketing-card">
          <h4>Ride the Free Launch</h4>
          <p>Courses are free during the launch offer — a perfect time to attract your first students and gather reviews.</p>
        </div>
      </div>
    </div>
  );

  const renderEngagement = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faUsers} /> Student Engagement</h2>

      <div className="engagement-strategies">
        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h4>Build Community</h4>
          <p>Encourage student interaction through discussions, Q&A sessions, and peer feedback opportunities.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faStar} />
          </div>
          <h4>Provide Feedback</h4>
          <p>Respond to student questions promptly and provide constructive feedback on their progress.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <h4>Track Progress</h4>
          <p>Use Performance and Total Students to monitor engagement and completion, and spot where students drop off.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faRotate} />
          </div>
          <h4>Keep It Fresh</h4>
          <p>Regularly update your lessons based on student feedback and industry changes, then push them live with Update Course.</p>
        </div>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
    case 'getting-started':
      return renderGettingStarted();
    case 'course-creation':
      return renderCourseCreation();
    case 'content-creation':
      return renderContentCreation();
    case 'pricing-publishing':
      return renderPricingPublishing();
    case 'manage-grow':
      return renderManageGrow();
    case 'best-practices':
      return renderBestPractices();
    case 'marketing':
      return renderMarketing();
    case 'engagement':
      return renderEngagement();
    default:
      return renderGettingStarted();
    }
  };

  return (
    <div>
      <InstaTopbar />
      <div className="instructor-guide-container">

        <div className="guide-content">
          <div className="guide-sidebar">
            <h3>Guide Sections</h3>
            <ul className="section-nav">
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    className={`section-button ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <FontAwesomeIcon icon={section.icon} />
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="guide-main">
            <button
              className="close-guide-btn"
              onClick={() => navigate('/dashboard')}
              aria-label="Close Guide"
            >
                            ×
            </button>
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorGuide;
